package org.example.project.service

import androidx.compose.runtime.mutableStateListOf
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import org.example.project.dashboard.DashboardNode
import org.example.project.dashboard.ScreenManager
import org.example.project.manager.LumaticaApiManager
import org.example.project.utils.All

private const val SHOP_PURCHASED_KEY = "shopPurchased"
private const val CUSTOMER_STATE_PURCHASED_KEY = "customerModulePurchased"
private const val CUSTOMER_STATE_INSTALLED_KEY = "customerModuleInstalled"
private const val CUSTOMER_STATE_INSTALLED_VERSION_KEY = "customerModuleInstalledVersion"
private const val CUSTOMER_STATE_STORE_VERSION_KEY = "customerModuleStoreVersion"
private const val CUSTOMER_STATE_UPDATE_AVAILABLE_KEY = "customerModuleUpdateAvailable"
private const val CUSTOMER_STATE_AVAILABLE_SCREENS_KEY = "customerModuleAvailableScreens"
private const val CUSTOMER_STATE_HAS_STORE_MODULE_KEY = "customerModuleHasStoreModule"

typealias CustomerModuleState = All

val CustomerModuleState.moduleId: Int get() = modulId
val CustomerModuleState.customerModule: All get() = module.firstOrNull() ?: All()
val CustomerModuleState.storeModule: All?
    get() = module.getOrNull(1)
val CustomerModuleState.purchased: Boolean get() = shopPurchased
val CustomerModuleState.installed: Boolean get() = modulInstalliert
val CustomerModuleState.installedVersion: String? get() = shopInstalledVersion.ifBlank { null }
val CustomerModuleState.storeVersion: String? get() = shopStoreVersion.ifBlank { null }
val CustomerModuleState.availableScreens: List<String>
    get() = modulScreens.filterValues { it }.keys.toList()

object LizenzService {
    private val installedShopScreens = mutableStateListOf<String>()
    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = false
    }

    private fun currentScreenConfig(): Map<String, Map<String, String>> =
        ScreenManager.currentScreenConfig()

    private fun dashboardCusLicensedScreens(): List<String> =
        ScreenManager.licensableCusScreenKeys()

    fun isFreeScreen(screenKey: String): Boolean =
        currentScreenConfig()[if (screenKey == "AufgabenViewer") "MitarbeiterKalender" else screenKey]
            ?.get("lizenzpflichtig") == "false"

    fun isShopScreen(screenKey: String): Boolean =
        !isFreeScreen(screenKey)

    fun getCustomerDashboardScreens(): List<String> =
        ScreenManager.allScreenKeys(DashboardNode.CUS)

    fun getFreeScreens(): List<String> =
        dashboardCusLicensedScreens().filter(::isFreeScreen)

    fun getShopConfiguredScreens(): List<String> =
        dashboardCusLicensedScreens().filter(::isShopScreen)

    fun getCustomerShopScreens(): List<String> =
        getShopConfiguredScreens()

    fun getInstalledShopScreens(): List<String> =
        installedShopScreens.toList()

    fun getEffectiveScreens(): List<String> {
        val dashboard = dashboardCusLicensedScreens().toSet()
        val free = getFreeScreens()
        val installed = installedShopScreens.filter { it in dashboard }
        val effective = (free + installed).distinct()
        return if ("MitarbeiterKalender" in effective) (effective + "AufgabenViewer").distinct() else effective
    }

    fun applyCustomerModulesFromSync(modules: List<All>) {
        val activeShopScreens = modules
            .filter { it.modulInstalliert }
            .flatMap { modul -> modul.modulScreens.filterValues { enabled -> enabled }.keys }
            .distinct()

        installedShopScreens.clear()
        installedShopScreens.addAll(activeShopScreens)
        GlobalManager.triggerSessionConfigReload()
    }

    suspend fun loadCustomerScreenPrefs() {
    }

    private fun normalizeScreens(raw: Map<String, Boolean>, allowedShopScreens: Set<String>): Map<String, Boolean> =
        raw.filter { (key, enabled) -> enabled && key in allowedShopScreens }

    private fun compareVersionStrings(left: String, right: String): Int {
        val a = left.trim().ifBlank { "0.0.0" }.split(Regex("[^0-9A-Za-z]+"))
            .filter { it.isNotBlank() }
        val b = right.trim().ifBlank { "0.0.0" }.split(Regex("[^0-9A-Za-z]+"))
            .filter { it.isNotBlank() }
        val max = maxOf(a.size, b.size)
        for (index in 0 until max) {
            val av = a.getOrNull(index)
            val bv = b.getOrNull(index)
            if (av == null) return -1
            if (bv == null) return 1
            val an = av.toIntOrNull()
            val bn = bv.toIntOrNull()
            val cmp = when {
                an != null && bn != null -> an.compareTo(bn)
                else -> av.compareTo(bv)
            }
            if (cmp != 0) return cmp
        }
        return 0
    }

    private suspend fun loadCanonicalCustomerModules(firmenId: String?, persist: Boolean): List<All> {
        val target = firmenId?.takeIf { it.isNotBlank() } ?: "~"
        val raw = runCatching {
            LumaticaApiManager.call("lizenz/customer-modules/${apiPart(target)}/${persist}")
        }.getOrDefault("")
        val synced = if (raw.isBlank() || raw == "null") emptyList()
        else runCatching { json.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())

        applyCustomerModulesFromSync(synced)

        return synced
    }

    suspend fun syncKundenModuleState(firmenId: String): List<All> =
        loadCanonicalCustomerModules(firmenId, persist = true)

    suspend fun syncKundenModuleState(): List<All> =
        loadCanonicalCustomerModules(null, persist = true)

    suspend fun loadKundenModuleState(firmenId: String): List<All> =
        loadCanonicalCustomerModules(firmenId, persist = false)

    suspend fun loadKundenModuleState(): List<All> =
        loadCanonicalCustomerModules(null, persist = false)

    suspend fun loadCustomerModuleStateMap(firmenId: String): Map<Int, CustomerModuleState> {
        val raw = runCatching { LumaticaApiManager.call("lizenz/customer-module-map/${apiPart(firmenId)}") }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return emptyMap()
        return runCatching { json.decodeFromString<List<All>>(raw) }
            .getOrDefault(emptyList())
            .associateBy { it.modulId }
    }

    suspend fun loadCustomerModuleStateMap(): Map<Int, CustomerModuleState> {
        val raw = runCatching { LumaticaApiManager.call("lizenz/customer-module-map/~") }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return emptyMap()
        return runCatching { json.decodeFromString<List<All>>(raw) }
            .getOrDefault(emptyList())
            .associateBy { it.modulId }
    }


    suspend fun loadActiveCustomerUserCount(): Int {
        val raw = runCatching { LumaticaApiManager.call("lizenz/active-user-count/~") }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return 0
        return runCatching { json.parseToJsonElement(raw).jsonObject["count"]?.jsonPrimitive?.content?.toIntOrNull() ?: 0 }.getOrDefault(0)
    }
    suspend fun syncAllKundenModuleStates() {
        LumaticaApiManager.call("lizenz/sync-all")
    }

    private fun apiPart(value: String): String =
        js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String
}


